/* ============================================================================
   curvs-pdpd.js — CURVS Denim PDP behavior (sections/curvs-pdpd-*.liquid)
   Delegated document-level singleton (window.__cvPdpd) per CONVENTIONS.md §7:
   the theme editor swaps section DOM at any time, so nothing is bound to
   elements at parse time and no element references are cached across events.
   Per-render config lives in <script type="application/json" data-cv-pdpd-config>.

   Stock-state derivation (the 4-state UI), read only once a size is picked;
   before that the line reports cfg.productAvailable and the ATC stays inert:
     out      = no matching variant OR !variant.available
     preorder = product tagged "preorder" AND variant.available AND inventory
                tracked AND quantity <= 0 (i.e. selling on "continue" policy)
     low      = inventory tracked AND 0 < quantity < lowThreshold (default 10)
     in       = everything else
   ========================================================================== */
(function () {
  if (window.__cvPdpd) return;
  window.__cvPdpd = true;

  var ADDED_RESET_MS = 1800;

  /* ---------- helpers ----------------------------------------------------- */

  /* Same resolver the three sibling controllers use (curvs-cart.js,
     curvs-bb.js, curvs-pdpi.js). `/` on every current market, but a
     locale-prefixed market would make a hardcoded '/cart/add.js' the one
     add-to-cart on the site posting to the wrong path. */
  /* After a successful /cart/add.js: wake whichever cart drawer this market runs.
     US/CA have the CURVS drawer (curvs-cart.js listens to curvs:cart:*). NL/DE
     keep Impact's native drawer, which opens on `variant:add` and re-renders on
     `cart:refresh` — Impact's own product form fires exactly these two (with
     items + the cart JSON in detail), so the drawer cannot tell the difference.
     Only fired when no CURVS drawer is on the page, so the US flow is untouched. */
  function notifyCartAdded(added) {
    document.dispatchEvent(new CustomEvent('curvs:cart:changed'));
    document.dispatchEvent(new CustomEvent('curvs:cart:open'));
    if (document.querySelector('[data-cv-cart]')) return;
    var items = added && added.items ? added.items : (added ? [added] : []);
    fetch(routesRoot() + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: items, cart: cart } }));
      })
      .catch(function () {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: items } }));
      });
  }

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* CSS `scroll-behavior: auto !important` under prefers-reduced-motion does NOT
     override an explicit behavior passed to scrollTo(), so ask the query here. */
  function motionBehavior() {
    return (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
      ? 'auto' : 'smooth';
  }

  /* theme.liquid ships a polite live region; the size grid and the gallery both
     changed state in silence before this. */
  function announce(msg) {
    var el = document.getElementById('status-announcement');
    if (!el || !msg) return;
    el.textContent = '';
    window.setTimeout(function () { el.textContent = msg; }, 60);
  }

  function getRoot(el) {
    return el && el.closest ? el.closest('[data-cv-pdpd]') : null;
  }

  function rootById(sectionId) {
    return document.querySelector('[data-cv-pdpd][data-section-id="' + sectionId + '"]');
  }

  function getCfg(root) {
    if (!root) return null;
    var el = root.querySelector('[data-cv-pdpd-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function eq(a, b) {
    return String(a == null ? '' : a).trim().toLowerCase() ===
           String(b == null ? '' : b).trim().toLowerCase();
  }

  function fm(cents) {
    if (window.Curvs && typeof window.Curvs.formatMoney === 'function') {
      return window.Curvs.formatMoney(cents);
    }
    return ((cents || 0) / 100).toFixed(2);
  }

  function findVariant(cfg, size, color) {
    for (var i = 0; i < cfg.variants.length; i++) {
      var v = cfg.variants[i];
      if (!eq(v.size, size)) continue;
      if (cfg.hasColor && color != null && v.color != null && !eq(v.color, color)) continue;
      return v;
    }
    return null;
  }

  function stockState(cfg, v) {
    if (!v || !v.available) return 'out';
    if ((cfg.preorder || v.plan) && v.tracked && v.qty <= 0) return 'preorder';
    if (v.tracked && v.qty > 0 && v.qty < cfg.lowThreshold) return 'low';
    return 'in';
  }

  /* '' means the customer has not picked a size yet — the default state, since the
     section only pre-selects on a ?variant= deep link or a single-size product. */
  function currentSize(root, cfg) { return root.dataset.size || cfg.selectedSize || ''; }
  function currentColor(root, cfg) {
    if (!cfg.hasColor) return null;
    return root.dataset.color || cfg.selectedColor || null;
  }

  function activeMap(root, cfg) {
    return (root.dataset.finderMode || 'us') === 'us' ? cfg.usMap : cfg.waistMap;
  }

  /* ---------- back-in-stock capture (STOQ) --------------------------------
     The sold-out CTA used to flash "WE'LL EMAIL YOU" and capture nothing, so
     the majority of size taps on this store ended in a promise with no
     mechanism behind it. STOQ (app handle restockrocket-1) is installed on the
     shop and its app embed is enabled in config/settings_data.json, which puts
     restockrocket-product.js on the storefront. That bundle exposes:

       window._Stoq.openModal(product, variantId)   // _RestockRocket = same object

     Measured on the live storefront: openModal(null, 49469559537999) opens the
     modal with XL preselected, and submitting it POSTs
     {"intent":{"shopify_variant_id":49469559537999, ...},"customer":{"email":...}}
     to app.restockrocket.io/api/v1/intents.json.

     Two traps this call site is written around:
     1. The variant MUST be the SECOND argument. STOQ's wrapper forwards its
        three parameters positionally with `null` defaults, and the inner
        function's `variantId = config.selected_variant_id` default only fires
        on `undefined` — so openModal() with no arguments (and any amount of
        setting config.selected_variant_id first) silently subscribes the
        customer to the product's FIRST variant, not the size they tapped.
     2. The app bundle is deferred, so on an early tap it may not exist yet:
        wait for it (button held aria-busy) before falling back to an error. */
  var STOQ_WAIT_MS = 4000;
  var STOQ_POLL_MS = 150;
  var bisPendingSid = null; /* section awaiting a STOQ submit -> gets the flash */

  function stoqApi() {
    return window._Stoq || window._RestockRocket || null;
  }

  function setAtcBusy(sid, on) {
    var r = rootById(sid);
    if (!r) return;
    r.querySelectorAll('[data-cv-pdpd-atc]').forEach(function (b) {
      if (on) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
    });
  }

  /* Same inline error channel the add-to-cart failure path uses. */
  function bisError(sid, cfg) {
    var r = rootById(sid);
    var hint = r && r.querySelector('[data-cv-pdpd-hint]');
    if (!hint) return;
    hint.setAttribute('data-error-lock', '');
    hint.textContent = cfg.errorText;
    window.setTimeout(function () {
      var r2 = rootById(sid);
      var h2 = r2 && r2.querySelector('[data-cv-pdpd-hint]');
      if (h2) { h2.removeAttribute('data-error-lock'); updateAll(r2); }
    }, 4000);
  }

  function openBackInStock(sid, cfg, variantId) {
    /* Cross-builder hook: anything else that wants the demand signal (a second
       BIS provider, analytics) can listen instead of patching this file. */
    document.dispatchEvent(new CustomEvent('curvs:bis:request', {
      detail: { variantId: variantId || null }
    }));
    /* Parity with STOQ's own button, which publishes this before opening. */
    if (window.Shopify && window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish('stoq_button_clicked', {
        variant_id: variantId || null,
        channel: 'notify_me'
      });
    }

    var started = Date.now();
    setAtcBusy(sid, true);
    (function attempt() {
      var api = stoqApi();
      if (api && typeof api.openModal === 'function') {
        setAtcBusy(sid, false);
        bisPendingSid = sid;
        try {
          api.openModal(null, variantId || null);
        } catch (e) {
          bisPendingSid = null;
          bisError(sid, cfg);
        }
        return;
      }
      if (Date.now() - started >= STOQ_WAIT_MS) {
        setAtcBusy(sid, false);
        bisError(sid, cfg);
        return;
      }
      window.setTimeout(attempt, STOQ_POLL_MS);
    })();
  }

  /* The "WE'LL EMAIL YOU" state is now only reachable once an address has
     actually been captured. STOQ dispatches these on `window`. */
  window.addEventListener('stoq:restock-modal:submitted', function () {
    var sid = bisPendingSid;
    bisPendingSid = null;
    if (sid) flashAdded(sid);
  });
  window.addEventListener('stoq:restock-modal:closed', function () {
    bisPendingSid = null;
  });

  /* 1.8s confirmation flash (added / emailed), keyed on section id because the
     section DOM can be replaced between the click and the flash. */
  function flashAdded(sid) {
    var r = rootById(sid);
    if (!r) return;
    r.dataset.added = '1';
    updateAll(r);
    window.setTimeout(function () {
      var r2 = rootById(sid);
      if (!r2) return;
      r2.dataset.added = '';
      updateAll(r2);
    }, ADDED_RESET_MS);
  }

  /* ---------- master render pass ------------------------------------------ */

  /* quantity tiers (offer launch 2026-09): selected quantity + line total */
  function tierQty(root) {
    var el = root && root.querySelector('[data-cv-pdpd-tiers] input:checked');
    return el ? (parseInt(el.value, 10) || 1) : 1;
  }
  function tierTotal(root, unit) {
    var q = tierQty(root);
    return q === 2 ? Math.round(unit * 1.5) : q === 3 ? unit * 2 : unit;
  }

  function updateAll(root) {
    var cfg = getCfg(root);
    if (!cfg) return;
    var size = currentSize(root, cfg);
    var color = currentColor(root, cfg);
    var v = findVariant(cfg, size, color);
    /* No size picked yet is NOT "sold out": findVariant returns null in both cases,
       but only one of them means the product is unbuyable. Before a size is chosen
       the stock line talks about the product, the ATC keeps its price and goes
       aria-disabled, and the hint asks for a size. */
    var hasSize = !!size;

    /* Kaching bridge: mirror the selected variant into the hidden form's
       [name=id] — Kaching hooks that value and re-renders its own per-unit
       selects from it. Only assign on real changes; before a size is picked
       the SSR value (current variant) stands. */
    var kIdIn = root.querySelector('[data-cv-pdpd-kaching-id]');
    if (kIdIn && v && String(v.id) !== kIdIn.value) kIdIn.value = String(v.id);
    var state = hasSize ? stockState(cfg, v) : (cfg.productAvailable === false ? 'out' : 'in');

    /* size grid */
    root.querySelectorAll('[data-cv-pdpd-size]').forEach(function (btn) {
      var label = btn.getAttribute('data-size');
      var sv = findVariant(cfg, label, color);
      var soldOut = !sv || !sv.available;
      var selected = eq(label, size);
      btn.classList.toggle('is-selected', selected);
      btn.classList.toggle('is-soldout', soldOut);
      btn.classList.toggle('cv-strike', soldOut);
      /* State was carried by the classes alone: no aria-pressed, no availability. */
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (soldOut) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
      var outNote = btn.querySelector('[data-cv-pdpd-size-out]');
      if (outNote) outNote.hidden = !soldOut;
    });

    /* selected size name */
    root.querySelectorAll('[data-cv-pdpd-sizename]').forEach(function (el) {
      el.textContent = size || cfg.selectPrompt;
    });

    /* stock line */
    var stockEl = root.querySelector('[data-cv-pdpd-stock]');
    if (stockEl) {
      stockEl.className = 'cv-pdpd-stock cv-pdpd-stock--' + state;
      var stockText = state === 'preorder' ? ((v && v.plabel) || cfg.preorderLabel) : cfg.labels[state];
      if (state === 'low' && v && stockText && stockText.indexOf('{count}') >= 0) stockText = stockText.replace('{count}', v.qty);
      var labelEl = stockEl.querySelector('[data-cv-pdpd-stock-label]');
      if (labelEl) labelEl.textContent = stockText;
    }

    /* price */
    var priceWrap = root.querySelector('[data-cv-pdpd-price]');
    if (priceWrap && v) {
      var html = '';
      var onSale = v.compare && v.compare > v.price;
      if (onSale) html += '<s class="cv-price__was">' + fm(v.compare) + '</s>';
      html += '<span class="cv-price__current">' + fm(v.price) + '</span>';
      if (onSale) {
        var pct = Math.round(((v.compare - v.price) / v.compare) * 100);
        html += '<span class="cv-price__save">' + cfg.saveWord + ' ' + pct + '%</span>';
      }
      /* Replace only the price span: the offer badge (LABOR DAY SALE) is a
         sibling inside the same wrapper and a wholesale innerHTML write wiped
         it on the first size selection (Simon, 2026-08-29). */
      var priceSpan = '<span class="cv-price cv-price--pdp">' + html + '</span>';
      var priceEl = priceWrap.querySelector('.cv-price');
      if (priceEl) priceEl.outerHTML = priceSpan;
      else priceWrap.insertAdjacentHTML('afterbegin', priceSpan);
    }

    /* ATC labels (buy box + sticky bar share [data-cv-pdpd-atc-label]) */
    var added = root.dataset.added === '1';
    var atcText;
    if (state === 'out') atcText = added ? cfg.atc.emailed : cfg.atc.notify;
    else if (!hasSize) atcText = cfg.showPrice === false ? cfg.atc.add : cfg.atc.add + ' · ' + fm(cfg.basePrice);
    else if (state === 'preorder') atcText = added ? cfg.atc.added : cfg.atc.reserve;
    else atcText = added ? cfg.atc.added : (cfg.showPrice === false ? cfg.atc.add : cfg.atc.add + ' · ' + fm(tierTotal(root, v ? v.price : cfg.basePrice)));
    root.querySelectorAll('[data-cv-pdpd-atc-label]').forEach(function (el) {
      el.textContent = atcText;
    });

    /* quantity tiers: recompute row prices/savings from the live unit price.
       Display only — the automatic discount at checkout is the source of truth. */
    var tiersEl = root.querySelector('[data-cv-pdpd-tiers]');
    if (tiersEl) {
      var unit = v ? v.price : cfg.basePrice;
      var tp = { 1: unit, 2: Math.round(unit * 1.5), 3: unit * 2 };
      var ts = { 2: Math.round(unit * 0.5), 3: unit };
      tiersEl.querySelectorAll('[data-cv-tier-price]').forEach(function (el) {
        el.textContent = fm(tp[el.getAttribute('data-cv-tier-price')]);
      });
      tiersEl.querySelectorAll('[data-cv-tier-save]').forEach(function (el) {
        el.textContent = fm(ts[el.getAttribute('data-cv-tier-save')]);
      });
    }
    /* Inert until a size is picked. Same treatment the intimates PDP gives its ATC:
       no visual change, so the buy box does not shift, but the click is a no-op and
       assistive tech is told so. A sold-out product keeps a live "notify me". */
    root.querySelectorAll('[data-cv-pdpd-atc]').forEach(function (btn) {
      if (hasSize || state === 'out') btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
    });

    /* hint line */
    var hintEl = root.querySelector('[data-cv-pdpd-hint]');
    if (hintEl && !hintEl.hasAttribute('data-error-lock')) {
      hintEl.textContent = state === 'out' ? (hasSize ? cfg.hints.out : '')
        : !hasSize ? cfg.hints.size
          : state === 'preorder' ? cfg.hints.preorder : '';
    }

    /* inline size-chart highlight (desktop) */
    root.querySelectorAll('[data-cv-pdpd-chart-row]').forEach(function (row) {
      row.classList.toggle('is-active', eq(row.getAttribute('data-curvs'), size));
    });

    /* fit-finder trigger label */
    var trig = root.querySelector('[data-cv-pdpd-finder-trigger-label]');
    if (trig) {
      var derived = root.dataset.finderCurvs || '';
      trig.textContent = derived ? cfg.finder.triggerHit + ' ' + derived : cfg.finder.trigger;
    }
  }


  /* ---------- model note ---------------------------------------------------
     Port of the live theme's model-info-inline script (curvjeans.com PDP).
     Reads the model's stats off the ACTIVE gallery image and writes the line
     under the size head. Three sources, in the live theme's order of
     precedence: hashtag alt, pipe alt, then the filename token. An image with
     no model data writes nothing — no stand-in sentence.
     ------------------------------------------------------------------------ */

  function miParseNum(value) {
    if (!value) return null;
    var match = String(value).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  }

  function miCleanName(value) {
    if (!value) return null;
    return String(value).trim().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  }

  function miCleanSize(value) {
    if (!value) return null;
    return String(value).trim().replace(/^size\s*/i, '').toUpperCase();
  }

  function miHeight(cm, useInches) {
    if (!cm) return '';
    if (!useInches) return Math.round(cm) + 'cm';
    var total = Math.round(cm * 0.3937008);
    return Math.floor(total / 12) + '’' + (total % 12) + '”';
  }

  function miWaist(cm, useInches) {
    if (!cm) return '';
    if (!useInches) return Math.round(cm) + 'cm';
    return Math.round(cm * 0.3937008) + '”';
  }

  function miHasData(data) {
    if (!data) return false;
    if (Array.isArray(data)) return data.length > 0;
    return !!(data.name || data.size || data.hCm != null || data.wCm != null);
  }

  function miParseHashtags(alt) {
    var result = { name: null, size: null, hCm: null, wCm: null };
    var tags = (alt || '').match(/#[^\s]+/g) || [];
    tags.forEach(function (tag) {
      var lower = tag.toLowerCase();
      if (/^#model[_\-:]?/.test(lower)) result.name = miCleanName(tag.replace(/^#model[_\-:]?/i, ''));
      if (/^#size[_\-:]?/.test(lower)) result.size = miCleanSize(tag.replace(/^#size[_\-:]?/i, ''));
      if (/^#h(?:eight)?[_\-:]?/.test(lower)) result.hCm = miParseNum(tag.replace(/^#h(?:eight)?[_\-:]?/i, ''));
      if (/^#w(?:aist)?[_\-:]?/.test(lower)) result.wCm = miParseNum(tag.replace(/^#w(?:aist)?[_\-:]?/i, ''));
    });
    return result;
  }

  /* Supported ALT formats:
       old: "L | 169cm | 74cm waist"
       new: "Delana | L | 169cm | 74cm waist" */
  function miParsePipes(alt) {
    var empty = { name: null, size: null, hCm: null, wCm: null };
    if (!alt) return empty;
    var parts = alt.trim().replace(/^\s*model\s*:\s*/i, '').split('|').map(function (part) {
      return part.trim();
    });
    if (parts.length >= 4) {
      return { name: miCleanName(parts[0]), size: miCleanSize(parts[1]), hCm: miParseNum(parts[2]), wCm: miParseNum(parts[3]) };
    }
    if (parts.length >= 3) {
      return { name: null, size: miCleanSize(parts[0]), hCm: miParseNum(parts[1]), wCm: miParseNum(parts[2]) };
    }
    return empty;
  }

  function miParseAlt(alt) {
    var hash = miParseHashtags(alt);
    if (miHasData(hash)) return [hash];
    var pipes = miParsePipes(alt);
    return miHasData(pipes) ? [pipes] : [];
  }

  /* Filename fallback: __mi-<SIZE>_<H>_<W>, or __miA-/__miB- for two models
     in one shot (each gets its own sentence). */
  function miParseFilename(src) {
    var output = [];
    if (!src) return output;
    try {
      var filename = new URL(src, location.origin).pathname.split('/').pop();
      filename = filename.split('?')[0].replace(/_(\d+)x(?=\.[a-z]+$)/i, '');

      (filename.match(/__mi[AB]-[a-z0-9\-+]+_[\d.,]+_[\d.,]+/gi) || []).forEach(function (token) {
        var m = token.match(/__mi([AB])-([a-z0-9\-+]+)_([\d.,]+)_([\d.,]+)/i);
        if (m) output.push({ name: null, id: m[1], size: miCleanSize(m[2]), hCm: miParseNum(m[3]), wCm: miParseNum(m[4]) });
      });

      if (!output.length) {
        var single = filename.match(/__mi-([a-z0-9\-+]+)_([\d.,]+)_([\d.,]+)(?=\.[a-z]+$|[_\-\.])/i);
        if (single) output.push({ name: null, id: 'A', size: miCleanSize(single[1]), hCm: miParseNum(single[2]), wCm: miParseNum(single[3]) });
      }
      return output;
    } catch (error) {
      return output;
    }
  }

  function miSentence(data, useInches, locale) {
    var name = '<span class="mi-name">' + (data.name || 'Model') + '</span>';
    var size = data.size ? '<span class="mi-size">' + data.size + '</span>' : '';
    var height = miHeight(data.hCm, useInches);
    var waist = miWaist(data.wCm, useInches);

    if (locale === 'nl') {
      if (height && waist && size) return name + ' is ' + height + ', de taille is ' + waist + ' en draagt maat ' + size + '.';
      if (height && size) return name + ' is ' + height + ' en draagt maat ' + size + '.';
      if (size) return name + ' draagt maat ' + size + '.';
      return '';
    }
    if (locale === 'de') {
      if (height && waist && size) return name + ' ist ' + height + ' groß, Taille ' + waist + ' und trägt Größe ' + size + '.';
      if (height && size) return name + ' ist ' + height + ' groß und trägt Größe ' + size + '.';
      if (size) return name + ' trägt Größe ' + size + '.';
      return '';
    }
    if (height && waist && size) return name + ' is ' + height + ', has a ' + waist + ' waist and wears size ' + size + '.';
    if (height && size) return name + ' is ' + height + ' and wears size ' + size + '.';
    if (size) return name + ' wears size ' + size + '.';
    return '';
  }

  function renderModelNote(root, source) {
    var note = root.querySelector('[data-cv-pdpd-modelnote]');
    if (!note) return;
    var html = '';
    if (source) {
      var data = miParseAlt(source.getAttribute('data-model-alt'));
      if (!miHasData(data)) data = miParseFilename(source.getAttribute('data-model-src'));
      if (miHasData(data)) {
        var useInches = root.dataset.useInches === 'true';
        var locale = root.dataset.locale || 'en';
        html = data.map(function (d) { return miSentence(d, useInches, locale); }).filter(Boolean).join(' ');
      }
    }
    /* innerHTML: the sentence carries .mi-name / .mi-size spans, and every part
       of it is theme/admin-authored (alt text and filenames), never shopper input. */
    if (note.innerHTML !== html) note.innerHTML = html;
  }

  function modelSourceAt(root, idx) {
    var slides = root.querySelectorAll('[data-cv-pdpd-slide]');
    if (slides[idx]) return slides[idx];
    var thumbs = root.querySelectorAll('[data-cv-pdpd-thumb][data-desktop]');
    return thumbs[idx] || null;
  }

  /* ---------- mobile gallery index ---------------------------------------- */

  function setMobileIndex(root, idx) {
    root.querySelectorAll('[data-cv-pdpd-dot]').forEach(function (d, i) {
      d.classList.toggle('is-active', i === idx);
    });
    root.querySelectorAll('[data-cv-pdpd-thumb]:not([data-desktop])').forEach(function (t) {
      var on = parseInt(t.getAttribute('data-index'), 10) === idx;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    renderModelNote(root, modelSourceAt(root, idx));
  }

  /* ---------- fit finder --------------------------------------------------- */

  function renderFinderMode(root) {
    var cfg = getCfg(root);
    if (!cfg) return;
    var mode = root.dataset.finderMode || 'us';
    var map = mode === 'us' ? cfg.usMap : cfg.waistMap;
    var title = root.querySelector('[data-cv-pdpd-finder-title]');
    if (title) title.textContent = mode === 'us' ? cfg.finder.usTitle : cfg.finder.waistTitle;
    var toggle = root.querySelector('[data-cv-pdpd-finder-mode]');
    if (toggle) toggle.textContent = mode === 'us' ? cfg.finder.toWaist : cfg.finder.toUs;
    var select = root.querySelector('[data-cv-pdpd-finder-select]');
    if (select) {
      var opts = '<option value="">' +
        (mode === 'us' ? cfg.finder.usPlaceholder : cfg.finder.waistPlaceholder) + '</option>';
      map.forEach(function (o) {
        opts += '<option value="' + String(o.value).replace(/"/g, '&quot;') + '">' + o.label + '</option>';
      });
      select.innerHTML = opts;
      select.value = '';
    }
    var result = root.querySelector('[data-cv-pdpd-finder-result]');
    if (result) {
      result.textContent = cfg.finder.result;
      result.classList.remove('is-hit');
    }
    root.dataset.finderCurvs = '';
    updateAll(root);
  }

  /* ---------- events ------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var t = e.target;

    /* gallery thumbs (mobile rail jump / desktop main swap) */
    var thumb = t.closest && t.closest('[data-cv-pdpd-thumb]');
    if (thumb) {
      var root = getRoot(thumb);
      if (!root) return;
      var idx = parseInt(thumb.getAttribute('data-index'), 10) || 0;
      if (thumb.hasAttribute('data-desktop')) {
        var main = root.querySelector('[data-cv-pdpd-main]');
        if (main) {
          if (thumb.getAttribute('data-full-src')) main.src = thumb.getAttribute('data-full-src');
          if (thumb.getAttribute('data-full-srcset')) main.srcset = thumb.getAttribute('data-full-srcset');
          /* data-img-alt is the media's own alt; data-alt is the visible model note. */
          if (thumb.getAttribute('data-img-alt') != null) main.alt = thumb.getAttribute('data-img-alt');
        }
        root.querySelectorAll('[data-cv-pdpd-thumb][data-desktop]').forEach(function (x) {
          var on = x === thumb;
          x.classList.toggle('is-active', on);
          x.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        announce(thumb.getAttribute('aria-label') || '');
        renderModelNote(root, thumb);
      } else {
        var rail = root.querySelector('[data-cv-pdpd-rail]');
        if (rail) rail.scrollTo({ left: idx * rail.clientWidth, behavior: motionBehavior() });
        setMobileIndex(root, idx);
      }
      return;
    }

    /* size buttons (sold-out stays selectable — stock UI + ATC handle it) */
    var sizeBtn = t.closest && t.closest('[data-cv-pdpd-size]');
    if (sizeBtn) {
      var root2 = getRoot(sizeBtn);
      if (!root2) return;
      root2.dataset.size = sizeBtn.getAttribute('data-size');
      root2.dataset.added = '';
      updateAll(root2);
      announce(
        'Size ' + sizeBtn.getAttribute('data-size') +
        (sizeBtn.classList.contains('is-soldout') ? ' selected, sold out' : ' selected')
      );
      return;
    }

    /* in-page color select (fallback mode when the product has a Color option;
       sibling-product colors are plain links and never reach here) */
    var colorBtn = t.closest && t.closest('[data-cv-pdpd-color]');
    if (colorBtn && colorBtn.hasAttribute('data-color-value')) {
      var root3 = getRoot(colorBtn);
      if (!root3) return;
      root3.dataset.color = colorBtn.getAttribute('data-color-value');
      root3.querySelectorAll('[data-cv-pdpd-color]').forEach(function (c) {
        c.classList.toggle('is-active', c === colorBtn);
      });
      var cname = root3.querySelector('[data-cv-pdpd-colorname]');
      if (cname) cname.textContent = colorBtn.getAttribute('data-color-value');
      var mainImg = root3.querySelector('[data-cv-pdpd-main]');
      if (mainImg && colorBtn.getAttribute('data-media-src')) {
        mainImg.src = colorBtn.getAttribute('data-media-src');
        if (colorBtn.getAttribute('data-media-srcset')) mainImg.srcset = colorBtn.getAttribute('data-media-srcset');
      }
      updateAll(root3);
      return;
    }

    /* fit finder open/close */
    var finderToggle = t.closest && t.closest('[data-cv-pdpd-finder-toggle]');
    if (finderToggle) {
      var root4 = getRoot(finderToggle);
      if (!root4) return;
      var panel = root4.querySelector('[data-cv-pdpd-finder-panel]');
      if (panel) {
        var open = panel.hasAttribute('hidden');
        if (open) panel.removeAttribute('hidden'); else panel.setAttribute('hidden', '');
        finderToggle.classList.toggle('is-open', open);
        finderToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
      return;
    }

    /* fit finder mode switch (US size <-> waist measurement) */
    var modeBtn = t.closest && t.closest('[data-cv-pdpd-finder-mode]');
    if (modeBtn) {
      var root5 = getRoot(modeBtn);
      if (!root5) return;
      root5.dataset.finderMode = (root5.dataset.finderMode || 'us') === 'us' ? 'waist' : 'us';
      renderFinderMode(root5);
      return;
    }

    /* accordions — exclusive open, prototype behavior */
    var accBtn = t.closest && t.closest('[data-cv-pdpd-acc]');
    if (accBtn) {
      var item = accBtn.closest('[data-cv-pdpd-acc-item]');
      var group = accBtn.closest('[data-cv-pdpd-accordions]');
      if (!item || !group) return;
      var wasOpen = item.classList.contains('is-open');
      group.querySelectorAll('[data-cv-pdpd-acc-item]').forEach(function (it) {
        var open = !wasOpen && it === item;
        it.classList.toggle('is-open', open);
        var body = it.querySelector('[data-cv-pdpd-acc-body]');
        if (body) { if (open) body.removeAttribute('hidden'); else body.setAttribute('hidden', ''); }
        var sign = it.querySelector('[data-cv-pdpd-acc-sign]');
        if (sign) sign.textContent = open ? '–' : '+';
        var btn = it.querySelector('[data-cv-pdpd-acc]');
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      return;
    }

    /* PAIRS_WITH "SELECT A SIZE" -> quick-add sheet (cart builder owns the sheet) */
    var qaBtn = t.closest && t.closest('[data-cv-pdpd-qa]');
    if (qaBtn) {
      document.dispatchEvent(new CustomEvent('curvs:quickadd:open', {
        detail: {
          handle: qaBtn.getAttribute('data-handle'),
          /* display pricing: product.js has no metafields, so the sheet needs
             the numbers the card beside it already rendered */
          salePct: parseInt(qaBtn.getAttribute('data-sale-pct'), 10) || 0,
          saleFixed: parseInt(qaBtn.getAttribute('data-sale-fixed'), 10) || 0
        }
      }));
      return;
    }

    /* "Size chart" link: desktop scrolls to the inline size guide; on mobile
       the guide is display:none, so the link falls through to its href */
    var sgLink = t.closest && t.closest('[data-cv-pdpd-sizechart-scroll]');
    if (sgLink) {
      var target = document.getElementById('size-guide');
      if (target && target.offsetParent !== null) {
        e.preventDefault();
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - 100,
          behavior: motionBehavior()
        });
      }
      return;
    }

    /* comparison CTA without a link -> scroll back to the buy box */
    var cmpBtn = t.closest && t.closest('[data-cv-pdpd-compare-cta]');
    if (cmpBtn && cmpBtn.tagName === 'BUTTON') {
      var buy = document.querySelector('[data-cv-pdpd-buybox]');
      if (buy) {
        window.scrollTo({
          top: buy.getBoundingClientRect().top + window.scrollY - 100,
          behavior: motionBehavior()
        });
      }
      return;
    }

    /* size-chart unit toggle: flips the root's data-use-inches, which also
       drives the model note — one unit state for the whole PDP. The modal
       clones the guide outside the root, so mirror the attr onto any open
       modal shell too. */
    var unitBtn = t.closest && t.closest('[data-cv-pdpd-unit]');
    if (unitBtn) {
      var unitVal = unitBtn.getAttribute('data-cv-pdpd-unit');
      var rootU = getRoot(unitBtn) || document.querySelector('[data-cv-pdpd]');
      if (rootU) rootU.setAttribute('data-use-inches', unitVal);
      document.querySelectorAll('[data-cv-size-modal]').forEach(function (m) {
        m.setAttribute('data-use-inches', unitVal);
      });
      return;
    }

    /* add to bag (buy box button + sticky mobile bar) */
    var atcBtn = t.closest && t.closest('[data-cv-pdpd-atc]');
    if (atcBtn) {
      var root6 = getRoot(atcBtn);
      var cfg = getCfg(root6);
      if (!root6 || !cfg) return;
      if (root6.dataset.added === '1' || atcBtn.hasAttribute('aria-busy')) return;
      var sid = root6.getAttribute('data-section-id');
      var size6 = currentSize(root6, cfg);
      var v = findVariant(cfg, size6, currentColor(root6, cfg));
      var state = size6 ? stockState(cfg, v) : (cfg.productAvailable === false ? 'out' : 'in');

      if (state === 'out') {
        /* Real back-in-stock capture, keyed on the size the customer tapped.
           `v` is null only when nothing is buyable at all (sold-out product,
           no size picked); STOQ's modal ships its own size select, so it can
           still take the address. The "WE'LL EMAIL YOU" flash is deferred to
           the stoq:restock-modal:submitted listener, so it now only appears
           once an address has actually been captured. */
        openBackInStock(sid, cfg, v ? v.id : null);
        return;
      }
      /* Kaching hosted (the bridge form wraps this button): the bundle widget
         owns quantity and per-unit sizes, so every add goes through the form.
         requestSubmit fires our JSON submit handler; Kaching's /cart/add
         interceptor rewrites that call to carry the full selected bundle —
         our own single-item fetch would silently add one pair of a two-pair
         selection (Simon's screenshot, 2026-08-29). */
      var kForm = root6.querySelector('[data-cv-pdpd-kaching-form]');
      if (kForm) {
        /* Only once the shopper has actually chosen — our grid, or a touch of
           Kaching's own selects (kachingTouched). Kaching preselects a size in
           its dropdowns, so a bare kId is not evidence of a decision and the
           "select your size" no-op below still guards the blind click. */
        var kId = kForm.querySelector('[data-cv-pdpd-kaching-id]');
        if (kId && Number(kId.value) > 0 && (size6 || root6.dataset.kachingTouched === '1')) {
          if (kForm.requestSubmit) kForm.requestSubmit();
          else kForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          return;
        }
      }
      /* No size picked: never guess one on the customer's behalf. The hint under
         the button already says what to do, so this is a deliberate no-op. */
      if (!size6) return;
      if (!v) return;

      root6.querySelectorAll('[data-cv-pdpd-atc]').forEach(function (b) {
        b.setAttribute('aria-busy', 'true');
      });
      /* Preorder adds must carry the STOQ selling plan or the line lands as a
         normal order that silently oversells and bypasses STOQ's deferred
         fulfillment (found 2026-08-24). */
      var item = { id: v.id, quantity: tierQty(root6) };
      if (v.plan && stockState(cfg, v) === 'preorder') item.selling_plan = v.plan;
      fetch(routesRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      }).then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      }).then(function (added) {
        notifyCartAdded(added);
        flashAdded(sid);
      }).catch(function () {
        var r = rootById(sid);
        if (!r) return;
        var hint = r.querySelector('[data-cv-pdpd-hint]');
        if (hint) {
          hint.setAttribute('data-error-lock', '');
          hint.textContent = cfg.errorText;
          setTimeout(function () {
            var r2 = rootById(sid);
            var h2 = r2 && r2.querySelector('[data-cv-pdpd-hint]');
            if (h2) { h2.removeAttribute('data-error-lock'); updateAll(r2); }
          }, 4000);
        }
      }).finally(function () {
        var r = rootById(sid);
        if (r) r.querySelectorAll('[data-cv-pdpd-atc]').forEach(function (b) {
          b.removeAttribute('aria-busy');
        });
      });
      return;
    }
  });

  /* fit finder select -> derive CURVS size, resolve to a real size value */
  document.addEventListener('change', function (e) {
    /* quantity tier picked -> refresh the ATC label total */
    if (e.target.matches && e.target.matches('[data-cv-pdpd-tiers] input')) {
      var tierRoot = getRoot(e.target);
      if (tierRoot) updateAll(tierRoot);
      return;
    }
    if (!e.target.matches || !e.target.matches('[data-cv-pdpd-finder-select]')) return;
    var root = getRoot(e.target);
    var cfg = getCfg(root);
    if (!root || !cfg) return;
    var map = activeMap(root, cfg);
    var val = e.target.value;
    var hit = null;
    for (var i = 0; i < map.length; i++) if (String(map[i].value) === val) { hit = map[i]; break; }
    var result = root.querySelector('[data-cv-pdpd-finder-result]');
    if (!hit) {
      if (result) { result.textContent = cfg.finder.result; result.classList.remove('is-hit'); }
      root.dataset.finderCurvs = '';
      updateAll(root);
      return;
    }
    if (result) {
      result.textContent = cfg.finder.result + ' ' + hit.curvs;
      result.classList.add('is-hit');
    }
    /* resolve derived label against the product's real size option values */
    var real = null;
    for (var j = 0; j < cfg.sizes.length; j++) if (eq(cfg.sizes[j], hit.curvs)) { real = cfg.sizes[j]; break; }
    root.dataset.finderCurvs = hit.curvs;
    if (real) {
      root.dataset.size = real;
      root.dataset.added = '';
      /* The panel STAYS OPEN on a hit (Simon, 2026-08-22). The prototype closed it,
         but the result box and the "93% choose their usual size" line live inside
         the panel, so closing it hid the answer the shopper just asked for and gave
         no chance to try another waist size. The size swatch above updates either
         way, so nothing is lost by leaving it open. */
    }
    updateAll(root);
  });

  /* mobile gallery rail — scroll does not bubble, so listen in capture phase */
  document.addEventListener('scroll', function (e) {
    var el = e.target;
    if (!el || el.nodeType !== 1 || !el.hasAttribute || !el.hasAttribute('data-cv-pdpd-rail')) return;
    var root = getRoot(el);
    if (!root) return;
    var idx = Math.round(el.scrollLeft / el.clientWidth);
    if (String(idx) !== root.dataset.slide) {
      root.dataset.slide = String(idx);
      setMobileIndex(root, idx);
    }
  }, true);

  /* First paint: the model note ships empty (an image with no model data must
     print nothing), so the parser has to fill it for the opening slide. */
  function bootModelNotes() {
    document.querySelectorAll('[data-cv-pdpd]').forEach(function (root) {
      var idx = parseInt(root.dataset.slide, 10) || 0;
      renderModelNote(root, modelSourceAt(root, idx));
    });
  }
  document.addEventListener('shopify:section:load', bootModelNotes);
  document.addEventListener('cv:section:load', bootModelNotes);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootModelNotes);
  } else {
    bootModelNotes();
  }

  /* Kaching bridge form: if Kaching (or anything) submits it, add via the same
     fetch path as the CURVS ATC so the drawer opens instead of a full-page POST.
     The form serializes whatever hidden fields Kaching injected (properties,
     selling_plan) alongside id/quantity. JSON, not FormData: Kaching's request
     interceptor rewrites the theme's /cart/add call to carry the FULL bundle,
     and it parses JSON item payloads — a multipart body sails through untouched,
     landing exactly one item (proven on the bra PDP, 2026-08-24). */
  document.addEventListener('submit', function (e) {
    var form = e.target instanceof Element ? e.target.closest('[data-cv-pdpd-kaching-form]') : null;
    if (!form) return;
    e.preventDefault();
    var root = getRoot(form);
    var cfg = getCfg(root);
    var sid = root && root.getAttribute('data-section-id');
    var item = { quantity: 1 };
    var props = null;
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.disabled) return;
      if (el.name === 'id') item.id = Number(el.value);
      else if (el.name === 'quantity') item.quantity = Number(el.value) || 1;
      else if (el.name === 'selling_plan' && el.value) item.selling_plan = Number(el.value) || el.value;
      else {
        var m = el.name.match(/^properties\[(.+)\]$/);
        if (m) { props = props || {}; props[m[1]] = el.value; }
      }
    });
    if (props) item.properties = props;
    if (!item.id) return;
    /* Preorder adds must still carry the STOQ selling plan (2026-08-24 bug):
       Kaching knows nothing about it, so look the plan up off the variant. */
    if (!item.selling_plan && cfg) {
      for (var pi = 0; pi < cfg.variants.length; pi++) {
        var pv = cfg.variants[pi];
        if (Number(pv.id) === item.id) {
          if (pv.plan && stockState(cfg, pv) === 'preorder') item.selling_plan = pv.plan;
          break;
        }
      }
    }
    if (sid) setAtcBusy(sid, true);
    fetch(routesRoot() + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        return res.json();
      })
      .then(function (added) { notifyCartAdded(added); })
      .catch(function () { /* leave the page as-is; Kaching shows its own error */ })
      .finally(function () { if (sid) setAtcBusy(sid, false); });
  }, true);

  /* ---------- Kaching two-way sync ----------
     Kaching's per-unit selects keep the bridge form's [name=id] current; mirror
     that back into the CURVS state so the size grid highlights, the "select your
     size" hint clears and the ATC arms — the same one-state feel the native
     tiers had. Kaching also rewrites the ATC label to a bare bundle total
     ("$163.50"); the normalizer keeps the verb in front of Kaching's money.
     Ported from the bra PDP (curvs-pdpi.js). */
  (function () {
    function init() {
      var form = document.querySelector('[data-cv-pdpd-kaching-form]');
      if (!form) return;
      var root = getRoot(form);
      var idIn = form.querySelector('[data-cv-pdpd-kaching-id]');
      if (!root || !idIn || root.dataset.cvKachingSync) return;
      root.dataset.cvKachingSync = '1';
      var lastId = idIn.value;
      function sync() {
        var cfg = getCfg(root);
        if (!cfg) return;
        if (idIn.value && idIn.value !== lastId) {
          lastId = idIn.value;
          for (var i = 0; i < cfg.variants.length; i++) {
            var v = cfg.variants[i];
            if (String(v.id) === String(idIn.value)) {
              if (v.size) root.dataset.size = v.size;
              if (cfg.hasColor && v.color != null) root.dataset.color = v.color;
              root.dataset.added = '';
              updateAll(root);
              break;
            }
          }
        }
        root.querySelectorAll('[data-cv-pdpd-atc]').forEach(function (btn) {
          var t = (btn.textContent || '').trim();
          if (t && !/[a-zA-Z]/.test(t)) btn.textContent = cfg.showPrice === false ? cfg.atc.add : cfg.atc.add + ' · ' + t;
        });
      }
      window.setInterval(sync, 350);
      document.addEventListener('change', function (e) {
        if (e.target && e.target.closest && e.target.closest('.cv-pdpd-appblocks')) {
          root.dataset.kachingTouched = '1';
          window.setTimeout(sync, 80);
        }
      }, true);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    document.addEventListener('shopify:section:load', function () { window.setTimeout(init, 0); });
  })();
})();

/* Sticky ATC reveal ------------------------------------------------------
 * Hidden in CSS, revealed only once the shopper has scrolled PAST the upsell
 * block (Simon, 2026-08-22 - it used to be on screen from the first paint,
 * competing with the buy box two thumb-widths above it).
 *
 * A rAF-throttled scroll read, NOT an IntersectionObserver: with threshold 0 the
 * observer only fires when the intersecting state flips, so jumping from below
 * the anchor straight back to the top (anchor never re-enters the viewport) left
 * the bar stuck open. Reading the rect on scroll is correct in both directions.
 *
 * Anchored to the upsell section, falling back to the in-flow ATC for products
 * that render no upsell rows. Document-level singleton per CONVENTIONS section 7.
 */
(function () {
  if (window.__cvPdpdStickyReveal) return;
  window.__cvPdpdStickyReveal = true;

  var ticking = false;
  var revealed = null;                       /* null = nothing applied yet */

  function anchorEl() {
    return document.querySelector('.cv-pdpd-pairs') || document.querySelector('.cv-pdpd-atc:not(.cv-pdpd-atc--sticky)');
  }

  function apply(next) {
    if (next === revealed) return;           /* don't touch the DOM on every frame */
    revealed = next;
    document.querySelectorAll('.cv-pdpd-sticky').forEach(function (bar) {
      bar.classList.toggle('is-revealed', next);
      bar.setAttribute('aria-hidden', next ? 'false' : 'true');
    });
  }

  function measure() {
    ticking = false;
    var el = anchorEl();
    if (!el) { apply(false); return; }
    apply(el.getBoundingClientRect().bottom <= 0);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(measure);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  document.addEventListener('shopify:section:load', measure);
  document.addEventListener('cv:section:load', measure);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', measure);
  } else {
    measure();
  }
})();
